import { ReactElement, useMemo, memo } from 'react';
import { useTheme } from '@mui/material';
import { LinkOff as UnLinkIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse as WarehouseIcon } from '@fortawesome/free-solid-svg-icons';

import { CollectItemStateTypes } from '~globals/types/enums';
import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import { PartnerCompanyItem } from '~services/partnerCompany/types';
import { validatePartnerItemCollectStateType } from '~utils/partnerItem';
import { isOnlyReadUser } from '~utils/user';
import { PATHS } from '~constants/paths';
import MenuActionsDataTable from '~components/MenuActionsDataTable';

import { useDashboardVariantV2CarrierContentCollectTabContext } from '../../Context';
import { ITEM_KEY_ID } from '../../utils';

export interface DashboardVariantV2CarrierContentCollectTabDataTableActionsMenuProps {
  data: PartnerCompanyItem;
}

const DashboardVariantV2CarrierContentCollectTabDataTableActionsMenu = ({
  data,
}: DashboardVariantV2CarrierContentCollectTabDataTableActionsMenuProps): ReactElement => {
  const { user } = useAppSelector(selectAuth);
  const currentTheme = useTheme();

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);

  const {
    dialogs: {
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
      confirmValidateOrder: { handleOpen: handleOpenConfirmValidateOrder },
      cancelOrder: { handleOpen: handleOpenDialogCancelOrder },
    },
  } = useDashboardVariantV2CarrierContentCollectTabContext();

  const enabledEdit = useMemo(
    () => !isUserOnlyReader && data.canModify,
    [isUserOnlyReader, data.canModify],
  );

  const enabledSetCollected = useMemo(
    () =>
      !isUserOnlyReader &&
      validatePartnerItemCollectStateType(data.collectItemStateTypeId, [
        CollectItemStateTypes.ToValidate,
        CollectItemStateTypes.ToCollect,
        CollectItemStateTypes.InCollect,
      ]),
    [data.collectItemStateTypeId, isUserOnlyReader],
  );

  const enabledCancel = useMemo(
    () =>
      !isUserOnlyReader &&
      validatePartnerItemCollectStateType(data.collectItemStateTypeId, [
        CollectItemStateTypes.ToValidate,
        CollectItemStateTypes.ToCollect,
        CollectItemStateTypes.InCollect,
        CollectItemStateTypes.NotValidated,
      ]),
    [data.collectItemStateTypeId, isUserOnlyReader],
  );

  return (
    <MenuActionsDataTable
      resource={{
        path: PATHS.ORDERS.BASE,
        id: data[ITEM_KEY_ID],
      }}
      items={{
        show: {
          enabled: true,
          onClick: () =>
            handleOpenDialogDetailsOrder({
              orderId: data[ITEM_KEY_ID],
              currentTab: null,
            }),
          divider: !enabledEdit,
        },
        edit: {
          enabled: enabledEdit,
          divider: enabledSetCollected || enabledCancel,
        },
      }}
      extraItems={[
        {
          enabled: enabledSetCollected,
          label: 'Marcar como recepcionado',
          Icon: (
            <FontAwesomeIcon
              icon={WarehouseIcon}
              color={currentTheme.palette.success.main}
            />
          ),
          onClick: () => handleOpenConfirmValidateOrder(data),
          divider: enabledCancel,
        },
        {
          enabled: enabledCancel,
          label: 'Desasignar colecta',
          Icon: <UnLinkIcon color="error" />,
          onClick: () => handleOpenDialogCancelOrder(data),
        },
      ]}
    />
  );
};

export default memo(
  DashboardVariantV2CarrierContentCollectTabDataTableActionsMenu,
);
